import {
    CalculationVersion,
    CalculationVersionEntityCode,
    Entity,
    PaymentVersion
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { ExecutionRequestDetailsRowData } from 'src/types/IExecutionRequestDetail';
import { IAppContextMetadata } from 'src/types/IMetadata';

export const createEntityIdMapping = (entities: Entity[]) => {
    return entities.reduce((accuEntities, currEntity) => {
        const entityVendorCodes = currEntity.entityVendorCodes?.reduce(
            (accuVendorCodes, currVendorCode) => ({
                ...accuVendorCodes,
                [currVendorCode.entityVendorCodeId!]: {
                    catalogVendorCode: currVendorCode.catalogVendorCode,
                    royaltyVendorCode: currVendorCode.royaltyVendorCode
                }
            }),
            {}
        );

        return {
            ...accuEntities,
            [currEntity.entityId!]: {
                entity: currEntity.entityName,
                entityVendorCodes: entityVendorCodes
            }
        };
    }, {} as any);
};

export const processCalculationVersions = (
    calculationVersions: CalculationVersion[],
    entityIdMapping: any,
    metadata: IAppContextMetadata
): ExecutionRequestDetailsRowData[] => {
    const incomingRowData: ExecutionRequestDetailsRowData[] = [];
    const groupedData = new Map<string, any[]>();

    calculationVersions.forEach((expandedExecutionRequest: CalculationVersion) => {
        expandedExecutionRequest.calculationVersionEntityCodes.forEach(
            (entityVendorCode: CalculationVersionEntityCode) => {
                entityVendorCode.paymentVersions.forEach((paymentVersion: PaymentVersion) => {
                    const groupKey = `${expandedExecutionRequest.expandedExecutionRequestId}-${expandedExecutionRequest.serviceId}-${expandedExecutionRequest.territoryId}-${expandedExecutionRequest.entityId}-${expandedExecutionRequest.month}-${entityVendorCode.entityVendorCodeId}`;

                    if (!groupedData.has(groupKey)) {
                        groupedData.set(groupKey, []);
                    }

                    groupedData.get(groupKey)!.push({
                        expandedExecutionRequestId: expandedExecutionRequest.expandedExecutionRequestId,
                        service: metadata.serviceMapping[expandedExecutionRequest.serviceId]?.serviceName ?? '',
                        territory: metadata.territoryMapping[expandedExecutionRequest.territoryId]?.territoryCode ?? '',
                        entity: entityIdMapping[expandedExecutionRequest.entityId]?.entity ?? '',
                        month: expandedExecutionRequest.month,
                        catalogVendorCode:
                            (entityIdMapping[expandedExecutionRequest.entityId.toString()]?.entityVendorCodes?.[
                                entityVendorCode.entityVendorCodeId.toString()
                            ]?.catalogVendorCode as string) ?? '',
                        royaltyVendorCode:
                            (entityIdMapping[expandedExecutionRequest.entityId.toString()]?.entityVendorCodes?.[
                                entityVendorCode.entityVendorCodeId.toString()
                            ]?.royaltyVendorCode as string) ?? '',
                        versionId: paymentVersion.versionId,
                        amount: paymentVersion.amount,
                        currency: paymentVersion.currency
                    });
                });
            }
        );
    });

    groupedData.forEach((versions) => {
        if (versions.length === 0) return;

        if (versions.length >= 2) {
            versions.sort((a, b) => a.versionId - b.versionId);
            const before = versions[0];
            const after = versions[versions.length - 1];

            incomingRowData.push({
                expandedExecutionRequestId: before.expandedExecutionRequestId,
                service: before.service,
                territory: before.territory,
                entity: before.entity,
                month: before.month,
                catalogVendorCode: before.catalogVendorCode,
                royaltyVendorCode: before.royaltyVendorCode,
                beforeAmount: before.amount,
                afterAmount: after.amount,
                beforeCurrency: before.currency,
                afterCurrency: after.currency,
                beforeVersionId: before.versionId,
                afterVersionId: after.versionId
            });
        } else if (versions.length === 1) {
            const version = versions[0];
            incomingRowData.push({
                expandedExecutionRequestId: version.expandedExecutionRequestId,
                service: version.service,
                territory: version.territory,
                entity: version.entity,
                month: version.month,
                catalogVendorCode: version.catalogVendorCode,
                royaltyVendorCode: version.royaltyVendorCode,
                beforeAmount: null,
                afterAmount: version.amount,
                beforeCurrency: null,
                afterCurrency: version.currency,
                beforeVersionId: null,
                afterVersionId: version.versionId
            });
        }
    });

    return incomingRowData;
};

export const processRoyaltyVersions = (
    royaltyVersions: any[],
    entityIdMapping: any,
    metadata: IAppContextMetadata,
    playTypeMapping: { [key: number]: string }
): any[] => {
    const processedRoyaltyData: any[] = [];

    royaltyVersions.forEach((version) => {
        version.royaltyVersionEntityCodes.forEach((entityCode: any) => {
            entityCode.royaltyVersionData.forEach((data: any) => {
                processedRoyaltyData.push({
                    ...data,
                    serviceId: version.serviceId,
                    service: metadata.serviceMapping[version.serviceId]?.serviceName || version.serviceId.toString(),
                    territoryId: version.territoryId,
                    entityId: version.entityId,
                    entity: entityIdMapping[version.entityId.toString()]?.entity || version.entityId.toString(),
                    month: version.month,
                    entityVendorCodeId: entityCode.entityVendorCodeId,
                    playTypeName: playTypeMapping[data.playTypeId] || data.playTypeId.toString()
                });
            });
        });
    });

    return processedRoyaltyData;
};

export const processRawCalculationVersions = (
    calculationVersions: CalculationVersion[],
    entityIdMapping: any,
    metadata: IAppContextMetadata
): any[] => {
    const rawData: any[] = [];
    const groupedData = new Map<string, any[]>();

    calculationVersions.forEach((version) => {
        version.calculationVersionEntityCodes.forEach((entityCode) => {
            entityCode.paymentVersions.forEach((paymentVersion) => {
                const groupKey = `${version.expandedExecutionRequestId}-${version.serviceId}-${version.territoryId}-${version.entityId}-${version.month}-${entityCode.entityVendorCodeId}`;

                if (!groupedData.has(groupKey)) {
                    groupedData.set(groupKey, []);
                }

                groupedData.get(groupKey)!.push({
                    versionId: paymentVersion.versionId,
                    service: metadata.serviceMapping[version.serviceId]?.serviceName ?? '',
                    territory: metadata.territoryMapping[version.territoryId]?.territoryCode ?? '',
                    entity: entityIdMapping[version.entityId]?.entity ?? '',
                    month: version.month,
                    catalogVendorCode:
                        entityIdMapping[version.entityId.toString()]?.entityVendorCodes?.[
                            entityCode.entityVendorCodeId.toString()
                        ]?.catalogVendorCode ?? '',
                    royaltyVendorCode:
                        entityIdMapping[version.entityId.toString()]?.entityVendorCodes?.[
                            entityCode.entityVendorCodeId.toString()
                        ]?.royaltyVendorCode ?? '',
                    amount: paymentVersion.amount,
                    currency: paymentVersion.currency
                });
            });
        });
    });

    // Only take the latest version (rerun result) from each group
    groupedData.forEach((versions) => {
        if (versions.length > 0) {
            versions.sort((a, b) => a.versionId - b.versionId);
            rawData.push(versions[versions.length - 1]);
        }
    });

    return rawData;
};

export const processRawRoyaltyData = (selectedRowRoyalties: any[]) => {
    const groupedData = new Map<string, any>();
    selectedRowRoyalties.forEach((royalty) => {
        const groupKey = [
            royalty.royaltyVendorCode,
            royalty.catalogVendorCode,
            royalty.royaltyModelCode,
            royalty.territoryCode,
            royalty.paperPlanName,
            royalty.playTypeId,
            royalty.programTypeName
        ].join('|');

        const existing = groupedData.get(groupKey);
        if (!existing || royalty.versionId > existing.versionId) {
            groupedData.set(groupKey, royalty);
        }
    });

    const rows = Array.from(groupedData.values()).map((royalty) => ({
        playType: royalty.playTypeName || royalty.playTypeId,
        programType: royalty.programTypeName,
        paperPlan: royalty.paperPlanName,
        priorRateValue: '-',
        currentRateValue: royalty.rateValue,
        priorRateCardCurrency: '-',
        currentRateCardCurrency: royalty.rateCardCurrency,
        priorRateCardAmount: '-',
        currentRateCardAmount: royalty.rateCardAmount?.toFixed(2),
        priorSettlementCurrency: '-',
        currentSettlementCurrency: royalty.settlementCurrency,
        priorSettlementAmount: '-',
        currentSettlementAmount: royalty.settlementAmount?.toFixed(2)
    }));

    return {
        rowData: rows,
        versionInfo: {
            priorVersion: '-',
            currentVersion: selectedRowRoyalties[0]?.versionId?.toString() || '-'
        }
    };
};

export const processComparisonRoyaltyData = (selectedRowRoyalties: any[]) => {
    const groupedData = new Map<string, any[]>();
    selectedRowRoyalties.forEach((royalty) => {
        const groupKey = [
            royalty.royaltyVendorCode,
            royalty.catalogVendorCode,
            royalty.royaltyModelCode,
            royalty.territoryCode,
            royalty.paperPlanName,
            royalty.playTypeId,
            royalty.programTypeName
        ].join('|');

        if (!groupedData.has(groupKey)) {
            groupedData.set(groupKey, []);
        }
        groupedData.get(groupKey)!.push(royalty);
    });

    const rows: any[] = [];
    let priorVersion = '-';
    let currentVersion = '-';

    groupedData.forEach((royalties) => {
        if (royalties.length >= 2) {
            royalties.sort((a, b) => a.versionId - b.versionId);
            const before = royalties[0];
            const after = royalties[royalties.length - 1];

            priorVersion = before.versionId.toString();
            currentVersion = after.versionId.toString();

            rows.push({
                playType: after.playTypeName || after.playTypeId,
                programType: after.programTypeName,
                paperPlan: after.paperPlanName,
                priorRateValue: before.rateValue,
                currentRateValue: after.rateValue,
                priorRateCardCurrency: before.rateCardCurrency,
                currentRateCardCurrency: after.rateCardCurrency,
                priorRateCardAmount: before.rateCardAmount?.toFixed(2),
                currentRateCardAmount: after.rateCardAmount?.toFixed(2),
                priorSettlementCurrency: before.settlementCurrency,
                currentSettlementCurrency: after.settlementCurrency,
                priorSettlementAmount: before.settlementAmount?.toFixed(2),
                currentSettlementAmount: after.settlementAmount?.toFixed(2)
            });
        } else if (royalties.length === 1) {
            const single = royalties[0];
            currentVersion = single.versionId.toString();

            rows.push({
                playType: single.playTypeName || single.playTypeId,
                programType: single.programTypeName,
                paperPlan: single.paperPlanName,
                priorRateValue: '-',
                currentRateValue: single.rateValue,
                priorRateCardCurrency: '-',
                currentRateCardCurrency: single.rateCardCurrency,
                priorRateCardAmount: '-',
                currentRateCardAmount: single.rateCardAmount?.toFixed(2),
                priorSettlementCurrency: '-',
                currentSettlementCurrency: single.settlementCurrency,
                priorSettlementAmount: '-',
                currentSettlementAmount: single.settlementAmount?.toFixed(2)
            });
        }
    });

    return {
        rowData: rows,
        versionInfo: { priorVersion, currentVersion }
    };
};

/**
 * Converts amount/currency pairs in a row to USD using provided conversion rates
 */
export const convertRowToUSD = (row: any, conversionRates: Map<string, Map<string, number>>): any => {
    const monthRates = conversionRates.get(row.month);
    if (!monthRates) return row;

    const convertPair = (amount: any, currency: any) => {
        if (
            amount === null ||
            amount === undefined ||
            amount === '-' ||
            !currency ||
            currency === '-' ||
            currency === 'USD'
        ) {
            return { amount, currency: currency === '-' ? '-' : currency || 'USD' };
        }

        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        const rate = monthRates.get(currency);

        if (!rate) return { amount, currency };

        return {
            amount: typeof amount === 'string' ? (numAmount * rate).toFixed(2) : numAmount * rate,
            currency: 'USD'
        };
    };

    const converted: any = { ...row };

    // Main grid - comparison view
    if (row.afterAmount !== undefined) {
        const after = convertPair(row.afterAmount, row.afterCurrency);
        converted.afterAmount = after.amount;
        converted.afterCurrency = after.currency;
    }
    if (row.beforeAmount !== undefined) {
        const before = convertPair(row.beforeAmount, row.beforeCurrency);
        converted.beforeAmount = before.amount;
        converted.beforeCurrency = before.currency;
    }

    // Main grid - raw view
    if (row.amount !== undefined && row.currency !== undefined) {
        const raw = convertPair(row.amount, row.currency);
        converted.amount = raw.amount;
        converted.currency = raw.currency;
    }

    // Royalty modal - current
    if (row.currentRateCardAmount !== undefined) {
        const rateCard = convertPair(row.currentRateCardAmount, row.currentRateCardCurrency);
        converted.currentRateCardAmount = rateCard.amount;
        converted.currentRateCardCurrency = rateCard.currency;
    }
    if (row.currentSettlementAmount !== undefined) {
        const settlement = convertPair(row.currentSettlementAmount, row.currentSettlementCurrency);
        converted.currentSettlementAmount = settlement.amount;
        converted.currentSettlementCurrency = settlement.currency;
    }

    // Royalty modal - prior
    if (row.priorRateCardAmount !== undefined) {
        const priorRateCard = convertPair(row.priorRateCardAmount, row.priorRateCardCurrency);
        converted.priorRateCardAmount = priorRateCard.amount;
        converted.priorRateCardCurrency = priorRateCard.currency;
    }
    if (row.priorSettlementAmount !== undefined) {
        const priorSettlement = convertPair(row.priorSettlementAmount, row.priorSettlementCurrency);
        converted.priorSettlementAmount = priorSettlement.amount;
        converted.priorSettlementCurrency = priorSettlement.currency;
    }

    return converted;
};
